import { Injectable } from '@angular/core';
import {
  IComedero,
  ICreateComedero,
  IDispositivo,
  IDispositivoComedero,
  IQueryParam,
  IReporte,
  IReporteComedero,
  IUpdateComedero,
} from 'modelos/src';
import { Observable } from 'rxjs';
import { HelperService } from '../../../auxiliares/helper.service';
import { HttpService } from '../../../auxiliares/http.service';
import { IConfigs } from 'src/app/modulos/offline/configuracion-canal/bluetooth.service';
import { apiBase } from 'src/environments/environment';
const api = apiBase;

@Injectable({
  providedIn: 'root',
})
export class ComederosService {
  constructor(private http: HttpService) {}
  public config(deveui: string, config: IConfigs) {
    return this.http.post(`${api}/comandos/comederos/config`, {
      deveui,
      config,
    });
  }

  // Comederos
  public getComedero(id: string): Observable<IComedero> {
    return this.http.get(`${api}/comederos/${id}`);
  }
  public getComederos(queryParams?: IQueryParam): Observable<{
    data: IComedero[];
    total: number;
  }> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/comederos`, { params });
  }
  public createComedero(data: ICreateComedero): Observable<IComedero> {
    return this.http.post(`${api}/comederos`, data);
  }
  public updateComedero(
    id: string,
    data: IUpdateComedero,
  ): Observable<IComedero> {
    return this.http.put(`${api}/comederos/${id}`, data);
  }
  public deleteComedero(id: string): Observable<IComedero> {
    return this.http.delete(`${api}/comederos/${id}`);
  }

  public frecReporte(deveui: string, frecuencia: number) {
    return this.http.post(`${api}/comandos/frecReporte`, {
      deveui: deveui,
      frecuencia,
    });
  }

  // Reportes
  // public exportar(queryParams?: IQueryParam): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     let params = HelperService.getQueryParams(queryParams);

  //     this.http
  //       .get(`${api}/reportes/exportar`, { params, responseType: 'blob' })
  //       .subscribe(
  //         (response: any) => {
  //           try {
  //             const file = new File([response], 'registros-nuc.xlsx');
  //             saveAs(file);
  //             resolve();
  //           } catch (error) {
  //             console.error(error);
  //             resolve();
  //           }
  //         },
  //         (error: any) => {
  //           const reader: FileReader = new FileReader();
  //           reader.onloadend = (e) => {
  //             reject(JSON.parse(reader.result as string));
  //           };
  //           reader.readAsText(error.error);
  //         }
  //       );
  //   });
  // }

  public async exportarReportes(
    esTecnico: boolean,
    queryParams?: IQueryParam,
    filename = 'reportes.xlsx',
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);
    if (esTecnico) {
      await this.http.getFile(
        `${api}/reportes/exportar-tecnico/comederos`,
        filename,
        params,
      );
    } else {
      await this.http.getFile(
        `${api}/reportes/exportar/comederos`,
        filename,
        params,
      );
    }
  }

  // Helper

  public getTiempoPromedioHistoricoConsumo(
    queryParams: IQueryParam,
  ): Observable<any> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/comederos/consumo`, { params });
  }

  public getDistanciaLeida(reporte?: IReporteComedero) {
    if (reporte) return reporte.distanciaLeida || 0;
    return 0;
  }

  public getDistancia(reporte?: IReporteComedero) {
    if (reporte) return reporte.distanciaLeida || 0;
    return 0;
  }

  public getAltura(reporte?: IReporteComedero) {
    if (reporte) return reporte.alturaLeida || 0;
    return 0;
  }

  public getFechasExtremas(reportes: IReporte[]): {
    fechaMin: Date;
    fechaMax: Date;
  } {
    if (!reportes || reportes.length === 0) {
      const now = new Date();
      return { fechaMin: now, fechaMax: now };
    }

    const fechas = reportes.map(
      (reporte) => new Date(reporte.fecha ?? new Date()),
    );
    const fechaMin = new Date(
      Math.min(...fechas.map((date) => date.getTime())),
    );
    const fechaMax = new Date(
      Math.max(...fechas.map((date) => date.getTime())),
    );

    return { fechaMin, fechaMax };
  }

  private valorNegativo(reporte: IReporte) {
    const valores = reporte.reporte as IReporteComedero;
    const altura = this.getAltura(valores);

    return altura < 0;
  }

  private presenciaDeAnimal(reporte: IReporte, comedero?: IComedero) {
    const valores = reporte.reporte as IReporteComedero;
    const altura = this.getAltura(valores);
    const dispositivo = comedero?.dispositivos?.find(
      (d) => d.deveui === reporte.deveui,
    );
    const config = dispositivo?.config as IDispositivoComedero;
    const alturaMaxima = config?.alturaMaxima || 300;

    return altura >= alturaMaxima;
  }

  public getEnCuantoTiempoTerminaron(
    reportes: IReporte[],
    comedero: IComedero,
    intervalosDeLectura: any,
  ): string | null {
    const datos: { fecha: string; valor: number; reportes: IReporte[] }[] = [];

    // Redondea las fechas de reporte según el intervalo de lectura específico
    for (const reporte of reportes) {
      const fechaReporte = new Date(reporte.fecha!);
      const timestamp = fechaReporte.getTime();
      const dispositivo = intervalosDeLectura.find(
        (d: any) => d.deveui === reporte.deveui,
      );
      const intervaloMs = (dispositivo?.intervaloDeLectura || 5) * 60 * 1000;
      const resto = timestamp % intervaloMs;
      fechaReporte.setTime(timestamp - resto + intervaloMs);
      reporte.fecha = fechaReporte.toISOString();
    }

    // Determina la fecha límite basada en el intervalo de lectura más pequeño
    const intervalosMs = intervalosDeLectura.map(
      (d: any) => d.intervaloDeLectura * 60 * 1000,
    );
    const minIntervaloMs = Math.min(...intervalosMs, 5 * 60 * 1000); // Default to 5 minutes if no intervals found
    let time = new Date().getTime();
    const resto = time % minIntervaloMs;
    time = time - resto;
    const fechaLimite = new Date(time);

    // Crea los promedios por fecha y los filtramos por presencia de animal
    for (const reporte of reportes) {
      const valores = reporte.reporte as IReporteComedero;
      const fecha = valores.fechaFrecuencia!;

      if (fechaLimite.getTime() < new Date(fecha).getTime()) continue;

      const existeFecha = datos.some((p) => p.fecha === fecha);
      if (existeFecha) continue;

      const reportesDeLaFechaR = reportes.filter(
        (r) => r.reporte?.fechaFrecuencia === fecha,
      );

      // Deja solo el ultimo reporte por cada deveui, filtrando presencia de animal y normalizando valores negativos a 0
      const reportesDeLaFecha = Array.from(
        reportesDeLaFechaR
          .reduce((acc, reporte) => {
            // Filtra reportes con presencia de animal
            if (this.presenciaDeAnimal(reporte, comedero)) {
              return acc;
            }

            // Normaliza valores negativos a 0
            const valorNegativo = this.valorNegativo(reporte);
            if (valorNegativo) {
              if (reporte?.reporte) {
                reporte.reporte.alturaLeida = 0;
              }
            }

            // Deja solo el ultimo reporte por cada deveui
            if (
              !acc.has(reporte.deveui) ||
              acc.get(reporte.deveui).fecha < reporte.fecha!
            ) {
              acc.set(reporte.deveui, reporte);
            }
            return acc;
          }, new Map())
          .values(),
      );

      const suma = reportesDeLaFecha.reduce((a, b) => {
        const valores = b.reporte as IReporteComedero;
        return a + this.getAltura(valores);
      }, 0);
      const promedio = +(suma / reportesDeLaFecha.length / 10).toFixed(2);

      datos.push({ fecha, valor: promedio, reportes: reportesDeLaFecha });
    }

    // Ordena los promedios por fecha
    datos.sort((a, b) => {
      const aDate = new Date(a.fecha);
      const bDate = new Date(b.fecha);
      return aDate.getTime() - bDate.getTime();
    });

    let tiempoInicio: Date | null = null;
    let tiempoFin: Date | null = null;
    const tiempos: number[] = [];

    for (let i = 0; i < datos.length; i++) {
      const dato = datos[i];
      const altura = dato.valor;
      const fecha = new Date(dato.fecha ?? new Date());

      if (altura > 1 && !tiempoInicio && i > 0) {
        // Registrar el tiempo de inicio en el punto anterior
        const datoAnterior = datos[i - 1];
        tiempoInicio = new Date(datoAnterior.fecha ?? new Date());
      } else if (altura <= 1 && tiempoInicio) {
        // Registrar el tiempo de fin
        tiempoFin = fecha;
        const tiempoU = (tiempoFin.getTime() - tiempoInicio.getTime()) / 60000;
        tiempos.push(tiempoU);
        tiempoInicio = null;
        tiempoFin = null;
      }
    }

    // Si la última sesión no ha terminado, regístrala
    if (tiempoInicio) {
      const tiempoU =
        (new Date(datos[datos.length - 1].fecha).getTime() -
          tiempoInicio.getTime()) /
        60000;
      tiempos.push(tiempoU);
    }

    // Filtrar tiempos menores a 35 minutos ya que estos no son grandes periodos de tiempo
    const tiemposFiltrados = tiempos.filter((tiempo) => tiempo >= 35);
    if (tiemposFiltrados.length > 0) {
      const sumaTiempos = tiemposFiltrados.reduce(
        (acc, tiempo) => acc + tiempo,
        0,
      );
      const promedioTiempos = sumaTiempos / tiemposFiltrados.length;
      const minutosRedondeados = Math.ceil(promedioTiempos); // Redondear los minutos hacia arriba

      // Convertir minutos totales a horas y minutos
      const horas = Math.floor(minutosRedondeados / 60);
      const minutos = minutosRedondeados % 60;

      return `${horas}h ${minutos}m`;
    } else {
      return null;
    }
  }

  public getAlturaPromedio(reportes: IReporte[]): number {
    const suma = reportes?.reduce((acc, reporte) => {
      const valor = reporte.reporte as IReporteComedero;
      return acc + this.getAltura(valor);
    }, 0);
    const promedio = suma ? +(suma / reportes?.length / 10).toFixed(2) : 0;

    return promedio < 0 ? 0 : promedio;
  }

  private fechaHaceXHoras(horas: number) {
    const fecha = new Date();
    fecha.setHours(fecha.getHours() - horas);
    return fecha;
  }

  public estadoDispositivo(dato: IDispositivo) {
    // const limite = this.fechaHaceXHoras(6);
    // const ultimaComunicacion = dato.fechaUltimaComunicacion
    //   ? new Date(dato.fechaUltimaComunicacion)
    //   : null;
    // const ultimoReporte = dato.ultimoReporte?.fecha
    //   ? new Date(dato.ultimoReporte.fecha)
    //   : null;
    // const ultimaAlerta = dato.ultimaAlerta?.fecha
    //   ? new Date(dato.ultimaAlerta.fecha)
    //   : null;
    // // if (ultimaAlerta) {
    // //   if (!ultimoReporte || ultimaAlerta > ultimoReporte) {
    // //     return 'Alerta';
    // //   }
    // // }
    // if (!ultimaComunicacion || limite > ultimaComunicacion) {
    //   return 'Sin reportar';
    // }
    return 'Ok';
  }

  //

  public offsetHoras(fecha: string, offset: number): string {
    const fechaNormalizada = new Date(fecha);
    fechaNormalizada.setHours(fechaNormalizada.getHours() + offset);
    return fechaNormalizada.toISOString();
  }

  public normalizarFecha(fecha: string): string {
    const fechaNormalizada = new Date(fecha);
    fechaNormalizada.setHours(fechaNormalizada.getHours());
    fechaNormalizada.setMinutes(0, 0, 0);
    return fechaNormalizada.toISOString();
  }

  public normalizarFechaXMinutos(fecha: string, x: number): string {
    const fechaNormalizada = new Date(fecha);
    const minutos = fechaNormalizada.getMinutes();
    if (x) {
      fechaNormalizada.setMinutes(minutos - (minutos % x) + x, 0, 0);
      return fechaNormalizada.toISOString();
    } else {
      fechaNormalizada.setSeconds(0, 0);
      return fechaNormalizada.toISOString();
    }
  }

  public horasTranscurridas(fecha1: string, fecha2: string) {
    const date1 = new Date(fecha1).getTime();
    const date2 = new Date(fecha2).getTime();
    const dif = Math.abs(date2 - date1);
    return Math.trunc(dif / 1000 / 60 / 60);
  }

  public fechaEnXDiasALas00(dias: number): Date {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() + dias);
    fecha.setHours(0, 0, 0, 0);
    return fecha;
  }

  public fechaHaceXHorasALas00(horas: number): Date {
    const fecha = new Date();
    fecha.setHours(fecha.getHours() - horas);
    fecha.setMinutes(0, 0, 0);
    return fecha;
  }

  public fechaDesde15Min(): Date {
    const fecha = new Date(this.fechaHasta15Min());

    fecha.setMinutes(fecha.getMinutes() - 15, 0, 0);
    console.log(`fechaDesde15Min: ${fecha}`);

    return fecha;
  }

  public fechaHasta15Min(): Date {
    const fecha = new Date();

    const minutos = fecha.getMinutes();
    let faltantes = 0;
    if (minutos > 0 && minutos <= 15) {
      faltantes = minutos;
    } else if (minutos > 15 && minutos <= 30) {
      faltantes = 15 - minutos;
    } else if (minutos > 30 && minutos <= 45) {
      faltantes = 30 - minutos;
    } else if (minutos > 45) {
      faltantes = 45 - minutos;
    }

    // const mitad = 15 / 2;
    fecha.setMinutes(fecha.getMinutes() + faltantes, 0, 0);
    console.log(`fechaHasta15Min: ${fecha}`);

    return fecha;
  }

  public filtrarReporteMasNuevoPorDeveui(reportes: IReporte[]) {
    const reporteMasNuevoPorDeveui: IReporte[] = [];
    reportes.forEach((r) => {
      const existeDeveui = reporteMasNuevoPorDeveui.find(
        (u) => u.deveui === r.deveui,
      );

      const fechaNueva = r.fecha!;
      const fechaGuardada = existeDeveui?.fecha ?? '';

      if (!existeDeveui) {
        reporteMasNuevoPorDeveui.push(r);
      } else {
        if (fechaNueva > fechaGuardada) {
          const index = reporteMasNuevoPorDeveui.indexOf(existeDeveui);
          reporteMasNuevoPorDeveui[index] = r;
        }
      }
    });
    return reporteMasNuevoPorDeveui;
  }
}
